import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SessionService } from '../services/session.service';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { ClinicService } from '../services/clinic.service';

declare global {
  interface Window {
    MARKABLE_USER_NAME?: string;
  }
}

/**
 * Patient entry portal (Maccabi x MARKABLE). The woman lands here from the
 * doctor's SMS invite. Two views:
 *   landing  -> the marketing/onboarding page
 *   consent  -> the informed-consent form she signs before her first scan
 * "Let's start" moves landing -> consent; "I approve and start" -> /app/new-test.
 *
 * Public page (no auth guard): she may arrive before logging in. Mirrors the
 * placement of the doctor portal (app/doctor/maccabi) directly under :lang.
 */
@Component({
  selector: 'app-patient-portal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './patient-portal.component.html',
  styleUrl: './patient-portal.component.scss',
})
export class PatientPortalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  view: 'landing' | 'consent' = 'landing';

  @ViewChild('heroVideo') heroVideo?: ElementRef<HTMLVideoElement>;

  /** First name folded into the hero headline, or '' when unknown (no name assumed). */
  firstName = '';
  // Clinic behind the /app/patient/maccabi landing (the Maccabi program).
  private readonly clinicSlug = 'maccabi';

  // Consent checkboxes. cb1 + cb3 are required; cb2 (research) is an optional
  // positive opt-in that must NEVER be auto-checked by "approve all".
  consentRequiredData = false; // cb1
  consentResearch = false; // cb2 (optional)
  consentPrivacy = false; // cb3
  approveRequired = false; // master toggle -> sets ONLY cb1 + cb3

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public sessionService: SessionService,
    private translationService: TranslationService,
    private clinics: ClinicService,
  ) {}

  ngOnInit(): void {
    this.resolveFirstName();
    this.syncViewFromUrl();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.syncViewFromUrl();
      });
  }

  private heroPlayCleanup: (() => void)[] = [];
  private heroObjectUrl: string | null = null;
  private destroyed = false;

  /**
   * Force silent autoplay, bulletproofed. The HTML `muted` attribute is NOT
   * reliably reflected to the muted IDL property in Angular templates, so the
   * browser sees an un-muted autoplay, blocks it by policy, and the poster shows
   * instead of the video. We set the property in code AND retry play() on every
   * realistic trigger (ready-to-play, in-viewport, first user gesture) so that
   * on devices/connections where the first attempt is throttled or policy-
   * blocked the clip still starts the moment it can.
   */
  ngAfterViewInit(): void {
    const el = this.heroVideo?.nativeElement;
    if (!el) return;
    el.muted = true;
    el.defaultMuted = true;
    el.setAttribute('muted', '');
    el.playsInline = true;
    // Legacy iOS (<10) inline-playback flag; harmless on modern Safari.
    el.setAttribute('webkit-playsinline', '');

    let playing = false;
    const tryPlay = () => {
      if (playing) return;
      el.muted = true;
      // iOS over cellular ignores preload="auto" and never buffers until a
      // load()+play() is requested inside a gesture; kick the load explicitly.
      if (el.readyState < 2) {
        try {
          el.load();
        } catch {
          /* noop */
        }
      }
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          playing = true;
        }).catch(() => {
          /* still blocked (e.g. iOS Low Power Mode); a later gesture retries */
        });
      }
    };

    tryPlay();
    for (const evt of ['loadeddata', 'canplay', 'canplaythrough'] as const) {
      el.addEventListener(evt, tryPlay);
      this.heroPlayCleanup.push(() => el.removeEventListener(evt, tryPlay));
    }

    // Play once it scrolls into view (handles deferred/throttled decode).
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) tryPlay();
      });
      io.observe(el);
      this.heroPlayCleanup.push(() => io.disconnect());
    }

    // iOS Low Power Mode blocks ALL autoplay (even muted/no-audio) until a real
    // user gesture. Keep retrying on EVERY gesture (not once) until play()
    // actually resolves, so an early scroll before the clip is ready isn't a
    // wasted single shot. Also retry when the tab becomes visible again.
    const onGesture = () => {
      if (playing) return;
      tryPlay();
    };
    const gestureEvents = ['touchstart', 'pointerdown', 'click', 'scroll'] as const;
    for (const evt of gestureEvents) {
      window.addEventListener(evt, onGesture, { passive: true });
      this.heroPlayCleanup.push(() => window.removeEventListener(evt, onGesture));
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') tryPlay();
    };
    document.addEventListener('visibilitychange', onVisible);
    this.heroPlayCleanup.push(() => document.removeEventListener('visibilitychange', onVisible));

    // iOS-reliability path: our staging host answers HTTP Range requests with
    // 200 (full body) instead of 206 Partial Content. Desktop Chrome tolerates
    // that, but iOS Safari's media loader requires a 206 to play a <video> from
    // a URL and otherwise just shows the poster (tapping does nothing). Bypass
    // ranged streaming entirely: fetch the clip once as a Blob and play it from
    // an in-memory object URL, which needs no range support on any browser.
    const source = el.querySelector('source');
    const srcUrl =
      source?.src ||
      new URL('assets/maccabi/facescan-hero-mesh.mp4', document.baseURI).toString();
    fetch(srcUrl)
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((blob) => {
        if (this.destroyed) return;
        this.heroObjectUrl = URL.createObjectURL(blob);
        el.src = this.heroObjectUrl;
        try {
          el.load();
        } catch {
          /* noop */
        }
        tryPlay();
      })
      .catch(() => {
        /* keep the original <source> path (works on browsers that honor range) */
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.heroPlayCleanup.forEach((fn) => fn());
    this.heroPlayCleanup = [];
    if (this.heroObjectUrl) {
      URL.revokeObjectURL(this.heroObjectUrl);
      this.heroObjectUrl = null;
    }
  }

  /** Source order: ?name= link param (Maccabi SMS) -> window global -> logged-in profile. */
  private resolveFirstName(): void {
    const raw = (
      this.route.snapshot.queryParamMap.get('name') ||
      window.MARKABLE_USER_NAME ||
      this.sessionService.session?.user?.fname ||
      ''
    ).trim();
    this.firstName = raw ? raw.split(/\s+/)[0] : '';
  }

  private syncViewFromUrl(): void {
    this.view = /\/patient(?:\/maccabi)?\/consent(?:[?#]|$)/.test(this.router.url)
      ? 'consent'
      : 'landing';
    if (this.view === 'consent') {
      try {
        window.scrollTo({ top: 0 });
      } catch {
        /* noop */
      }
    }
  }

  private currentLang(): string {
    const match = this.router.url.match(/^\/(en|he)(?:\/|$)/);
    return match?.[1] || this.translationService.getCurrentLanguage() || 'he';
  }

  /** "Let's start" -> consent screen. */
  goToConsent(): void {
    const patientSegments = this.isMaccabiPatientRoute()
      ? ['app', 'patient', 'maccabi', 'consent']
      : ['app', 'patient', 'consent'];
    this.router.navigate([`/${this.currentLang()}`, ...patientSegments]);
  }

  /** Back from the consent screen to the portal. */
  backToLanding(): void {
    const patientSegments = this.isMaccabiPatientRoute()
      ? ['app', 'patient', 'maccabi']
      : ['app', 'patient'];
    this.router.navigate([`/${this.currentLang()}`, ...patientSegments]);
  }

  private isMaccabiPatientRoute(): boolean {
    return /\/patient\/maccabi(?:[/?#]|$)/.test(this.router.url);
  }

  /**
   * Master "approve the required sections" toggle. By regulation (IL privacy +
   * Helsinki/IRB) the optional research consent must be a SEPARATE positive
   * choice, so this only ever drives the two REQUIRED boxes (cb1 + cb3) and
   * never touches the research box (cb2).
   */
  onApproveRequiredChange(checked: boolean): void {
    this.approveRequired = checked;
    this.consentRequiredData = checked;
    this.consentPrivacy = checked;
  }

  /** Keep the master box in sync if the user toggles a required box by hand. */
  onRequiredBoxChange(): void {
    this.approveRequired = this.consentRequiredData && this.consentPrivacy;
  }

  get canStart(): boolean {
    return this.consentRequiredData && this.consentPrivacy;
  }

  /** The three portal consent checkboxes as ticked, with the shown text, so the
   * server records each on the append-only consent ledger (incl. the optional
   * research box cb2, previously discarded). */
  private buildConsentDetail() {
    const t = (k: string) => this.translationService.translate(k);
    return {
      cb1: this.consentRequiredData,
      cb2: this.consentResearch,
      cb3: this.consentPrivacy,
      texts: {
        'portal.cb1': t('patientPortal.consent.cb1'),
        'portal.cb2': t('patientPortal.consent.cb2'),
        'portal.cb3': t('patientPortal.consent.cb3'),
      },
      locale: this.currentLang(),
    };
  }

  /** "I approve and start" -> join the clinic (consent given here) and begin a new test. */
  joining = false;
  async startTest(): Promise<void> {
    if (!this.canStart || this.joining) return;
    this.joining = true;
    const consentDetail = this.buildConsentDetail();
    try {
      if (this.isMaccabiPatientRoute()) {
        if (this.sessionService.session?.token) {
          try {
            const r: any = await this.clinics.patientJoin(
              this.clinicSlug,
              true,
              consentDetail,
            );
            const session = this.sessionService.session;
            if (r?.isMaccabi && session?.user) {
              (session.user as any).isMaccabi = true;
              (session.user as any).isPaid = true;
              (session.user as any).isEntitled = true;
              this.sessionService.saveSession(session);
            }
            // Free-TESTS clinics grant credits instead of unlimited access and
            // never set isMaccabi, so take the live entitlement from the join.
            this.sessionService.applyViewerEntitlement(r?.viewerEntitlement);
          } catch (e) {
            console.error('PatientPortalComponent: clinic join failed', e);
          }
        } else {
          // Guest: the join settles right after she logs in (spinner).
          this.clinics.setPending({
            slug: this.clinicSlug,
            invite: null,
            consent: true,
            consentDetail,
          });
        }
      }
    } finally {
      this.joining = false;
    }
    this.router.navigate([`/${this.currentLang()}`, 'app', 'new-test']);
  }
}
